// How undo and redo rewrite and delete a screen message (a UI element instance) through v1 (untracked), then bring
// the renderer and the editor along. Deleting one hides its clips (its trigger responses) with it, and restoring
// the deletion brings them back.

import type { components } from '@/generated/contracts/api-v1';
import type { RendererBridge } from '@/modules/rendererBridge';
import { v1 } from '@/infrastructure/api/v1/client';
import type { ScenePort } from './sceneWrites';
import { untracked } from './sceneWrites';
import type { SceneSync } from './scenePorts';
import { read, removeRow } from './v1Rows';

type UiElementInstanceDto = components['schemas']['UiElementInstanceDto'];

/** How the editor's state follows a changed screen message. */
export interface UiElementEditor {
  showUiElement(dto: UiElementInstanceDto): Promise<void> | void;
}

/** The undo stack's port for screen messages. */
export function uiElementPort(bridge: Pick<RendererBridge, 'send'>, editor: UiElementEditor, sync: SceneSync): ScenePort {
  return {
    get: id => read(v1.GET('/api/v1/ui-element-instances/{uniqueId}', { params: { path: { uniqueId: id } } })),
    async patch(id, fields) {
      const dto = await read(v1.PATCH('/api/v1/ui-element-instances/{uniqueId}', { params: { path: { uniqueId: id } }, body: fields, headers: untracked.headers }));
      bridge.send({ type: 'uiElementInstanceUpdated', uiElementInstance: dto });
      await editor.showUiElement(dto);
      return dto;
    },
    remove: id => removeRow(sync, v1.DELETE('/api/v1/ui-element-instances/{uniqueId}', { params: { path: { uniqueId: id } }, headers: untracked.headers })),
  };
}
